'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiRequirement } from '@shared/all/domains/factiiis';
import { type Post } from '@shared/all/domains/posts';
import { type Upload } from '@shared/all/domains/uploads';
import { FILE_INPUT_ACCEPT } from '@shared/all/uploadTypes';
import usePostBody from '@shared/utils/hooks/usePostBody';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useUpload from '@shared/utils/hooks/useUpload';
import type { Editor } from '@tiptap/react';
import { ImageIcon, Loader2 } from 'lucide-react';
import { useReducer, useRef, useState } from 'react';
import useContentUIRenderer from '../../hooks/useContentUIRenderer';
import { imageFilesFrom } from '../../utils/askImage';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import FormatTools from './FormatTools';
import { PostInput } from './LazyPostInput';
import { TOOLBAR_ICON, toolbarButton } from './toolbarButton';
import GalleryView from './UploadGalleryView';
type Props = {
  closeHandler: () => void;
  post: Post;
  uniqueId?: string;
};
type EditState = {
  buttonLoading: boolean;
  moderationConfModalOpen: boolean;
  uploadsChanged: boolean;
};
type EditAction = {
  type: 'SET_LOADING';
  value: boolean;
} | {
  type: 'SET_MODERATION';
  value: boolean;
} | {
  type: 'MARK_UPLOADS_CHANGED';
};
const editReducer = (state: EditState, action: EditAction): EditState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        buttonLoading: action.value
      };
    case 'SET_MODERATION':
      return {
        ...state,
        moderationConfModalOpen: action.value
      };
    case 'MARK_UPLOADS_CHANGED':
      return {
        ...state,
        uploadsChanged: true
      };
  }
};
const EditPostForm = props => {
  const $ = _c(52);
  const {
    filterText,
    removeTags
  } = useContentUIRenderer();
  const [editor, setEditor] = useState(null);
  const isBoardPost = props.post.pos != null;
  const [title, setTitle] = useState(props.post.title ?? "");
  let t0;
  if ($[0] !== filterText || $[1] !== props.post.content || $[2] !== props.post.id) {
    t0 = () => filterText({
      content: props.post.content,
      isFullLength: true,
      id: props.post.id,
      isEdit: true
    });
    $[0] = filterText;
    $[1] = props.post.content;
    $[2] = props.post.id;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const [content, setContent] = useState(t0);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      buttonLoading: false,
      moderationConfModalOpen: false,
      uploadsChanged: false
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [state, dispatch] = useReducer(editReducer, t1);
  const {
    editPost
  } = usePostsShared(client, props.uniqueId);
  const {
    files,
    addFile,
    removeFile,
    uploadingKeys,
    uploadOne
  } = useUpload(client);
  const fileInputRef = useRef(null);
  const cursorToEnd = _temp;
  let t2;
  if ($[5] !== props.post.uploads) {
    t2 = () => (props.post.uploads ?? []) as Upload[];
    $[5] = props.post.uploads;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const [existingUploads, setExistingUploads] = useState(t2);
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = id => {
      setExistingUploads(prev => prev.filter(u => u.id !== id));
      dispatch({
        type: "MARK_UPLOADS_CHANGED"
      });
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const removeExistingUpload = t3;
  let t4;
  if ($[8] !== removeFile) {
    t4 = key => {
      removeFile(key);
      dispatch({
        type: "MARK_UPLOADS_CHANGED"
      });
    };
    $[8] = removeFile;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const removeNewUpload = t4;
  let t5;
  if ($[10] !== addFile) {
    t5 = files_0 => {
      for (const file of files_0) {
        const key_0 = `edit-${Date.now()}-${file.name}`;
        const uri = URL.createObjectURL(file);
        addFile(key_0, {
          uri,
          fileName: file.name
        });
        dispatch({
          type: "MARK_UPLOADS_CHANGED"
        });
      }
    };
    $[10] = addFile;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const addFiles = t5;
  let t6;
  if ($[12] !== addFiles) {
    t6 = e_0 => {
      addFiles(Array.from(e_0.target.files ?? []));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
    $[12] = addFiles;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleFileChange = t6;
  let t7;
  if ($[14] !== addFiles) {
    t7 = e_1 => {
      const files_1 = imageFilesFrom(e_1.clipboardData);
      if (files_1.length === 0) {
        return;
      }
      e_1.preventDefault();
      addFiles(files_1);
    };
    $[14] = addFiles;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handlePaste = t7;
  let submitHandler;
  let t8;
  if ($[16] !== content || $[17] !== editPost || $[18] !== existingUploads || $[19] !== files || $[20] !== props || $[21] !== removeTags || $[22] !== state.buttonLoading || $[23] !== state.moderationConfModalOpen || $[24] !== state.uploadsChanged || $[25] !== title || $[26] !== uploadOne) {
    const moderationRequiredFactiiis = props.post.postFactiiisCount.filter(_temp2).map(_temp3);
    submitHandler = async force => {
      dispatch({
        type: "SET_LOADING",
        value: true
      });
      const contentChanged = props.post.content !== content;
      const titleChanged = (props.post.title ?? "") !== title;
      if (!contentChanged && !state.uploadsChanged && !titleChanged) {
        dispatch({
          type: "SET_LOADING",
          value: false
        });
        return;
      }
      if (moderationRequiredFactiiis.length > 0 && !force) {
        dispatch({
          type: "SET_MODERATION",
          value: true
        });
        dispatch({
          type: "SET_LOADING",
          value: false
        });
        return;
      }
      let serverUploads = null;
      if (state.uploadsChanged) {
        const newFileEntries = Object.values(files);
        const uploadedResults = [];
        for (const item of newFileEntries) {
          const result = await uploadOne(item.uri, item.fileName, undefined, {
            forPost: true
          });
          if (!result) {
            dispatch({
              type: "SET_LOADING",
              value: false
            });
            return;
          }
          uploadedResults.push(result);
        }
        serverUploads = [...existingUploads.map(_temp4), ...uploadedResults.map(_temp5)];
      }
      const success = await editPost(props.post.id, removeTags(content), props.post.isPrivate, undefined, serverUploads, titleChanged ? title.trim() || null : undefined);
      dispatch({
        type: "SET_MODERATION",
        value: false
      });
      dispatch({
        type: "SET_LOADING",
        value: false
      });
      if (success) {
        props.closeHandler();
      }
    };
    t8 = state.moderationConfModalOpen && <Confirmation title="Factiii Requires Re-Moderation" message={`This post contains factiiis (${moderationRequiredFactiiis.join(", ")}) that require moderation. Editing this post will require all previously approved moderated factiiis to be re-moderated. Do you want to proceed?`} actionButtonText="Continue Edit" cancelHandler={() => dispatch({
      type: "SET_MODERATION",
      value: false
    })} confirmHandler={() => submitHandler(true)} actionButtonLoading={state.buttonLoading} />;
    $[16] = content;
    $[17] = editPost;
    $[18] = existingUploads;
    $[19] = files;
    $[20] = props;
    $[21] = removeTags;
    $[22] = state.buttonLoading;
    $[23] = state.moderationConfModalOpen;
    $[24] = state.uploadsChanged;
    $[25] = title;
    $[26] = uploadOne;
    $[27] = submitHandler;
    $[28] = t8;
  } else {
    submitHandler = $[27];
    t8 = $[28];
  }
  let t9;
  if ($[29] !== content || $[30] !== editor || $[31] !== existingUploads || $[32] !== files || $[33] !== handleFileChange || $[34] !== handlePaste || $[35] !== isBoardPost || $[36] !== props.closeHandler || $[37] !== props.post.content || $[38] !== props.post.space || $[39] !== props.post.title || $[40] !== props.post.user || $[41] !== removeNewUpload || $[42] !== state.buttonLoading || $[43] !== state.moderationConfModalOpen || $[44] !== state.uploadsChanged || $[45] !== submitHandler || $[46] !== title || $[47] !== uploadingKeys) {
    t9 = !state.moderationConfModalOpen && <Modal closeHandler={props.closeHandler} maxWidth={isBoardPost ? 800 : 700}><ModalHeader closeHandler={props.closeHandler}><span className="text-lg font-semibold tracking-tight text-textPrimary">{isBoardPost ? "Edit Card" : "Edit Post"}</span></ModalHeader><ModalContent>{isBoardPost ? <div className="flex gap-1.5" onPaste={handlePaste}><Avatar avatarKey={props.post.user.avatarKey} robohash={props.post.user.robohash} isBot={props.post.user.tag === "BOT"} size={35} userId={props.post.user.id} isAnonymous={props.post.user.anonymous} /><div className="relative w-full space-y-3"><div><p className="mb-1.5 text-xs font-emphasis text-textSecondary">Title</p><input type="text" value={title} onChange={e_2 => {
                if (e_2.target.value.length <= 255) {
                  setTitle(e_2.target.value);
                }
              }} placeholder="Card title" maxLength={255} className="w-full rounded-lg border border-ds-border-standard bg-background-secondary px-3 py-2.5 text-base font-emphasis text-textPrimary outline-none transition-colors duration-200 placeholder:text-textSecondary/40 focus:border-primary" /></div><p className="text-xs font-emphasis text-textSecondary">Description</p><PostInput content={content} setContent={setContent} label="Edit description..." onFocus={cursorToEnd} onEditor={setEditor} /></div></div> : <div className="flex gap-3" onPaste={handlePaste}><Avatar avatarKey={props.post.user.avatarKey} robohash={props.post.user.robohash} isBot={props.post.user.tag === "BOT"} size={35} userId={props.post.user.id} isAnonymous={props.post.user.anonymous} /><div className="relative w-full"><PostInput content={content} setContent={setContent} label="Edit Post" onFocus={cursorToEnd} onEditor={setEditor} /></div></div>}<GalleryView uploads={[...existingUploads, ...Object.values(files)]} removeAttachmentHandler={id_0 => {
          if (existingUploads.some(u_2 => u_2.id === id_0)) {
            removeExistingUpload(id_0);
          } else {
            removeNewUpload(id_0);
          }
        }} uploadingKeys={uploadingKeys} /><input ref={fileInputRef} type="file" multiple={true} accept={FILE_INPUT_ACCEPT} onChange={handleFileChange} className="hidden" />{props.post.space && <p className="text-start">Posted at s/{props.post.space}</p>}</ModalContent><ModalFooter><div className="flex flex-wrap items-center gap-x-1 gap-y-2"><div className="flex grow items-center justify-between gap-0.5"><FormatTools editor={editor} />{editor && <span className="mx-0.5 h-5 w-px shrink-0 bg-ds-border-standard" />}</div><div className="flex min-w-0 grow items-center justify-between gap-0.5"><button type="button" title="Upload Media" className={toolbarButton()} onClick={() => fileInputRef.current?.click()}><ImageIcon size={TOOLBAR_ICON} /></button><div className="ml-auto shrink-0"><Button text="Save" onClick={() => submitHandler(false)} disabled={!content.trim() && !title.trim() || !state.uploadsChanged && props.post.content === content && (props.post.title ?? "") === title || state.buttonLoading} loading={state.buttonLoading} /></div></div></div></ModalFooter></Modal>;
    $[29] = content;
    $[30] = editor;
    $[31] = existingUploads;
    $[32] = files;
    $[33] = handleFileChange;
    $[34] = handlePaste;
    $[35] = isBoardPost;
    $[36] = props.closeHandler;
    $[37] = props.post.content;
    $[38] = props.post.space;
    $[39] = props.post.title;
    $[40] = props.post.user;
    $[41] = removeNewUpload;
    $[42] = state.buttonLoading;
    $[43] = state.moderationConfModalOpen;
    $[44] = state.uploadsChanged;
    $[45] = submitHandler;
    $[46] = title;
    $[47] = uploadingKeys;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] !== t8 || $[50] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[49] = t8;
    $[50] = t9;
    $[51] = t10;
  } else {
    t10 = $[51];
  }
  return t10;
};

// A DTO carries row 1 only; the form must mount on the whole body or a long
// post would be saved back as its first row.
const EditPost = props => {
  const $ = _c(20);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      all: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const body = usePostBody(client, props.post, t0);
  if (!body.complete) {
    const t1 = props.post.pos != null ? "Edit Card" : "Edit Post";
    let t2;
    if ($[1] !== t1) {
      t2 = <span className="text-lg font-semibold tracking-tight text-textPrimary">{t1}</span>;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== props.closeHandler || $[4] !== t2) {
      t3 = <ModalHeader closeHandler={props.closeHandler}>{t2}</ModalHeader>;
      $[3] = props.closeHandler;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== body.error) {
      t4 = body.error ? "Could not load this post." : <Loader2 className="size-6 animate-spin text-primary" />;
      $[6] = body.error;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== t4) {
      t5 = <ModalContent><div className="flex justify-center py-8 text-sm text-textSecondary">{t4}</div></ModalContent>;
      $[8] = t4;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== props.closeHandler || $[11] !== t3 || $[12] !== t5) {
      t6 = <Modal closeHandler={props.closeHandler} maxWidth={700}>{t3}{t5}</Modal>;
      $[10] = props.closeHandler;
      $[11] = t3;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  let t1;
  if ($[14] !== body.content || $[15] !== props.post) {
    t1 = {
      ...props.post,
      content: body.content
    };
    $[14] = body.content;
    $[15] = props.post;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  let t2;
  if ($[17] !== props || $[18] !== t1) {
    t2 = <EditPostForm {...props} post={t1} />;
    $[17] = props;
    $[18] = t1;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  return t2;
};
export default EditPost;
function _temp(e) {
  const el = e.currentTarget;
  const len = el.value.length;
  el.setSelectionRange(len, len);
}
function _temp2(factiii) {
  return factiii.requirements.includes(FactiiiRequirement.POSTFACTIII_MODERATION_REQUIRED);
}
function _temp3(factiii_0) {
  return factiii_0.name;
}
function _temp4(u_0) {
  return {
    id: u_0.id
  };
}
function _temp5(u_1) {
  return {
    id: u_1.id
  };
}